/*****************************************************************
 *  CUSTOMER BUTLER - CRM SETUP: WHICH BUILT-IN FIELDS SHOW        *
 *  File: apps/web/src/pages/customer/BuiltinFieldsCard.js         *
 *  Shown on: /app/crm-setup                                       *
 *  What it does: one switch per Butler built-in field (Full Name, *
 *  Tag, Status ...) for customers and for properties. Switching   *
 *  one asks why, saves it through the layout door, and the audit  *
 *  log keeps who, when and why. Fields Zoho's layout needs (first *
 *  name, phone, email ...) always show and say so.                *
 *  Custom fields keep their own switch in the field list below.   *
 *  Reads/Writes: GET/PUT /customer/layouts/<kind>/builtins.       *
 *****************************************************************/

/* WHY (2026-10-14, James; CLAUDE.md principle 9): every module's admin
   panel lets an owner choose "which fields are displayed". Custom fields
   could already be switched off; Butler's own could not. Who may use it is
   NOT decided here - the HR Team page owns permissions. */

import React, { useCallback, useEffect, useState } from 'react';
import { useButlerLook } from './customerLook';
import { QuietLine, Notice, StatusChip } from './customerParts';
import { fetchBuiltinChoices, saveBuiltinChoice } from './customerApi';
import './customer.css';

const RECORD_KINDS = [
  { entityType: 'Customer', label: 'Customer records' },
  { entityType: 'Property', label: 'Property records' },
];

/* ---- ChoiceRow({ choice, onFlip, isBusy }) -- one field's switch ---- */
function ChoiceRow({ choice, onFlip, isBusy }) {
  if (!choice.canHide) {
    return (
      <li className="cb-item">
        <div className="cb-item-top">
          <span className="cb-item-name">{choice.label}</span>
          <StatusChip tone="neutral" icon="check" word="Always shown" />
        </div>
        <div className="cb-item-meta">{choice.section}</div>
      </li>
    );
  }

  return (
    <li className="cb-item">
      <div className="cb-item-top">
        <label className="cb-choice">
          <input type="checkbox" checked={choice.isShown} disabled={isBusy} onChange={() => onFlip(choice)} />
          <span className="cb-item-name">{choice.label}</span>
        </label>
        <span className="cb-item-meta">{choice.isShown ? 'Shown' : 'Hidden'}</span>
      </div>
      <div className="cb-item-meta">{choice.section}</div>
    </li>
  );
}

/* ---- KindChoices({ entityType, label }) -- one record kind's list ---- */
function KindChoices({ entityType, label }) {
  const [choices, setChoices] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [noticeWords, setNoticeWords] = useState('');
  const [noticeTone, setNoticeTone] = useState('ok');

  const loadChoices = useCallback(async () => {
    try {
      setChoices(await fetchBuiltinChoices(entityType));
    } catch (loadError) {
      setChoices([]);
      setNoticeTone('bad');
      setNoticeWords('The field list could not be loaded.');
    }
  }, [entityType]);

  useEffect(() => {
    loadChoices();
  }, [loadChoices]);

  async function flipChoice(choice) {
    const willShow = !choice.isShown;
    const askWords = willShow ? `Show "${choice.label}" again. Why?` : `Hide "${choice.label}". Why?`;
    const reasonText = window.prompt(askWords, '');

    if (reasonText === null) {
      return;
    }

    setIsBusy(true);

    const saveAnswer = await saveBuiltinChoice(entityType, choice.key, willShow, reasonText);

    setIsBusy(false);

    if (!saveAnswer.ok) {
      setNoticeTone('bad');
      setNoticeWords((saveAnswer.error && saveAnswer.error.message) || 'That did not save.');
      return;
    }

    setNoticeTone('ok');
    setNoticeWords(`"${choice.label}" is now ${willShow ? 'shown' : 'hidden'} on every ${entityType === 'Customer' ? 'customer' : 'property'}.`);
    loadChoices();
  }

  return (
    <div>
      <h3 className="cb-item-name">{label}</h3>
      {noticeWords ? <Notice tone={noticeTone} icon={noticeTone === 'ok' ? 'check' : 'warn'}>{noticeWords}</Notice> : null}
      {choices === null ? <QuietLine icon="clock">Loading</QuietLine> : null}
      <ul className="cb-list-rows">
        {(choices || []).map((oneChoice) => (
          <ChoiceRow key={oneChoice.key} choice={oneChoice} onFlip={flipChoice} isBusy={isBusy} />
        ))}
      </ul>
    </div>
  );
}

/* ---- BuiltinFieldsCard() -- the whole card for CRM Setup ---- */
export function BuiltinFieldsCard() {
  useButlerLook();

  return (
    <section className="cb-page cb-setup">
      {/* Folded by default: CRM Setup is long, and this list is rarely changed. */}
      <details className="cb-section">
        <summary>Built-in fields: which ones show</summary>
        <div className="cb-setup-body">
          <p className="cb-sub">Butler's own fields, placed where Zoho has them. Switch off any you do not use; the stored values are kept.</p>
          {RECORD_KINDS.map((oneKind) => (
            <KindChoices key={oneKind.entityType} entityType={oneKind.entityType} label={oneKind.label} />
          ))}
        </div>
      </details>
    </section>
  );
}

export default BuiltinFieldsCard;
